import React, {useEffect, useState} from 'react';
import SwiperCore, {A11y, Autoplay, Navigation, Pagination, Scrollbar} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import UserService from '../../services/user.service';
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Autoplay]);
import 'swiper/swiper-bundle.min.css';
import {getLocaleContent} from '@helper/locale-content.js';
import {useRouter} from 'next/router';
import Link from 'next/link';
import {changeToSlug} from '@helper/string.helper';
import styles from '../SpecialistDoctor/RelatedDoctors.module.css';
import {useBooking} from '@contexts/BookingContext';
import {safeHtml} from '@helper/sanitize-html';

/**
 * Component hiển thị danh sách bác sĩ của chuyên khoa
 * Cho phép user click "Đặt lịch khám" để tự động fill form
 */
export default function SpecialtyDoctorList(props) {
    const [users, setUsers] = useState([]);
    const {locale} = useRouter();
    const {setBookingInfo} = useBooking();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768); // 768px là breakpoint phổ biến cho mobile
        };

        checkMobile(); // kiểm tra ngay khi load
        window.addEventListener('resize', checkMobile); // theo dõi resize

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const setTitleStyle = () => {
        // Set tiêu đề với kích thước mong muốn khi component được render
        if (!isMobile) return;
        const titleEl = document.querySelector('.doctors__title');
        console.log('titleEl', titleEl);
        if (titleEl) {
            titleEl.style.fontSize = '32px';
        }
        const doctosLead = document.querySelector('.doctors__lead');
        if (doctosLead) {
            doctosLead.style.fontSize = '14px';
        }
    };
    useEffect(() => {
        const department = Array.isArray(props?.preloadedDepartment)
            ? props.preloadedDepartment[0]
            : props?.preloadedDepartment;

        if (!department?.department_id) return;
        const userService = new UserService();
        userService
            .getUserList({
                locale: locale,
                pageSize: 100,
                pageIndex: 1,
                departmentId: department.department_id,
            })
            .then(response => {
                const items = response?.data?.items || response?.items;
                if (items && Array.isArray(items) && items.length > 0) {
                    setUsers(items);
                    setTitleStyle();
                } else {
                    setUsers([]);
                }
            })
            .catch(error => {
                console.error('Lỗi khi lấy danh sách bác sĩ:', error);
                setUsers([]);
            });
    }, [props?.preloadedDepartment?.department_id, locale]);

    const generateSlug = user => {
        const nameSlug = changeToSlug(user?.full_name);
        const id = user?.user_id;
        if (id) {
            return `${nameSlug}-u${id}`;
        }
        return '';
    };
    // Cấu hình Swiper cho slider bác sĩ
    const swiperConfig = {
        autoplay:
            users.length >= 3
                ? {
                      delay: 2500,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                  }
                : false,
        loop: users.length >= 3,
        spaceBetween: 14,
        navigation: {
            nextEl: `.${styles['next-button']}`,
            prevEl: `.${styles['previous-button']}`,
        },
        watchOverflow: true,
        observer: true,
        observeParents: true,
        slidesPerView: 'auto',
        onSwiper: swiper => {
            // Setup autoplay restart khi click navigation buttons
            if (users.length >= 3 && swiper.autoplay) {
                const restart = () => {
                    if (swiper.autoplay && swiper.autoplay.running === false) {
                        swiper.autoplay.start();
                    }
                };
                const nextBtn = document.querySelector(`.${styles['next-button']}`);
                const prevBtn = document.querySelector(`.${styles['previous-button']}`);
                if (nextBtn) {
                    nextBtn.addEventListener('click', () => setTimeout(restart, 50));
                }
                if (prevBtn) {
                    prevBtn.addEventListener('click', () => setTimeout(restart, 50));
                }
            }
        },
    };

    if (!users?.length) {
        return null;
    }

    return (
        <>
            <section className="doctors">
                <div className="container">
                    <header className="doctors__head">
                        <h2 className="doctors__title">
                            {getLocaleContent(locale, 'page.doctors.specialty-doctors-part1')}{' '}
                            <span>{getLocaleContent(locale, 'page.doctors.specialty-doctors-part2')}</span>{' '}
                            {getLocaleContent(locale, 'page.doctors.specialty-doctors-part3')}
                        </h2>
                        <p className="doctors__lead">{getLocaleContent(locale, 'page.doctors.lead')}</p>
                    </header>
                    <div className={styles['swiper-container']}>
                        <button
                            className={`${styles['previous-button']} navbtn bw_hover_next`}
                            aria-label="Prev"></button>
                        <button className={`${styles['next-button']} navbtn bw_hover_prev`} aria-label="Next"></button>

                        <div className={styles['doctor-carousel']}>
                            <Swiper {...swiperConfig} className={styles['swiper']}>
                                {users
                                    .filter(user => user && user.user_id)
                                    .map((user, index) => (
                                        <SwiperSlide
                                            key={user.user_id || index}
                                            style={{
                                                width: '317px',
                                                minWidth: '317px',
                                                flexShrink: 0,
                                            }}>
                                            <Link
                                                href={{
                                                    pathname: '/[slug]',
                                                    query: {slug: encodeURIComponent(generateSlug(user))},
                                                }}>
                                                <article className="doccard">
                                                    <div className="doccard__media">
                                                        <img src={user.image_url} alt={user.full_name} />
                                                    </div>
                                                    <div className="doccard__body">
                                                        <span className="doccard__tag">
                                                            {user.education_level_name || ' '}
                                                        </span>
                                                        <h3 className="doccard__name">{user.full_name}</h3>
                                                        <p className="doccard__meta">
                                                            {user.position_name} {' - '} {user.department_name}
                                                            {user?.business_name && (
                                                                <>
                                                                    {' '}
                                                                    <br />
                                                                    {user.business_name}
                                                                </>
                                                            )}
                                                            {user?.expertise && (
                                                                <>
                                                                    <br />
                                                                    {getLocaleContent(
                                                                        locale,
                                                                        'page.doctors.expertise',
                                                                    )}{' '}
                                                                    {user.expertise}
                                                                </>
                                                            )}
                                                        </p>
                                                        <button
                                                            className="doccard__cta"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                e.stopPropagation();

                                                                // Lấy thông tin chuyên khoa
                                                                const department = Array.isArray(
                                                                    props?.preloadedDepartment,
                                                                )
                                                                    ? props.preloadedDepartment[0]
                                                                    : props?.preloadedDepartment;

                                                                // Set dữ liệu đặt lịch cho bác sĩ
                                                                console.log(
                                                                    '[SpecialtyDoctorList] Click đặt lịch bác sĩ:',
                                                                    {
                                                                        user_id: user.user_id,
                                                                        full_name: user.full_name,
                                                                        department_id:
                                                                            department?.department_id ||
                                                                            user.department_id,
                                                                        department_name:
                                                                            department?.department_name ||
                                                                            user.department_name,
                                                                        business_id: user.business_id,
                                                                        user_name: user.user_name,
                                                                    },
                                                                );
                                                                setBookingInfo({
                                                                    department:
                                                                        department?.department_name ||
                                                                        user.department_name ||
                                                                        '',
                                                                    department_id:
                                                                        department?.department_id ||
                                                                        user.department_id ||
                                                                        '',
                                                                    doctor: user.full_name || '',
                                                                    user_id: user.user_id || '',
                                                                    business_id: user.business_id || '',
                                                                    user_name: user.user_name || '',
                                                                });

                                                                // Scroll xuống form đặt lịch
                                                                const formSection =
                                                                    document.querySelector('.register-section');
                                                                if (formSection) {
                                                                    const offset = 150; // Khoảng cách từ top
                                                                    const elementPosition = formSection.offsetTop;
                                                                    const offsetPosition = elementPosition - offset;

                                                                    window.scrollTo({
                                                                        top: offsetPosition,
                                                                        behavior: 'instant',
                                                                    });
                                                                }
                                                            }}>
                                                            <img
                                                                className="icon"
                                                                src="/bw_image/ic_calendar_w.png"
                                                                alt=""
                                                            />
                                                            {getLocaleContent(locale, 'page.doctors.booking')}
                                                        </button>
                                                    </div>
                                                    <div className="doccard__overlay">
                                                        <div className="doccard__ov-wrap">
                                                            {user.education_level_name && (
                                                                <span className="doccard__tag">
                                                                    {user.education_level_name}
                                                                </span>
                                                            )}
                                                            <h3 className="doccard__name">{user.full_name}</h3>
                                                            <p className="doccard__meta">
                                                                {user.position_name} {' - '} {user.department_name}
                                                            </p>
                                                            <p className="doccard__meta">
                                                                {user?.business_name && <>{user.business_name}</>}
                                                                {user?.expertise && (
                                                                    <>
                                                                        <br />
                                                                        {getLocaleContent(
                                                                            locale,
                                                                            'page.doctors.expertise',
                                                                        )}{' '}
                                                                        {user.expertise}
                                                                    </>
                                                                )}
                                                            </p>
                                                            <h4 className="doccard__overlay-title">
                                                                {getLocaleContent(
                                                                    locale,
                                                                    'page.doctors.experience-department',
                                                                )}
                                                            </h4>
                                                            <div
                                                                className="doccard__list"
                                                                dangerouslySetInnerHTML={safeHtml(
                                                                    user.about_me_short,
                                                                )}></div>
                                                            <button
                                                                className="doccard__cta"
                                                                onClick={e => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();

                                                                    // Lấy thông tin chuyên khoa
                                                                    const department = Array.isArray(
                                                                        props?.preloadedDepartment,
                                                                    )
                                                                        ? props.preloadedDepartment[0]
                                                                        : props?.preloadedDepartment;

                                                                    // Set dữ liệu đặt lịch cho bác sĩ (overlay)
                                                                    console.log(
                                                                        '[SpecialtyDoctorList] Click đặt lịch bác sĩ (overlay):',
                                                                        {
                                                                            user_id: user.user_id,
                                                                            full_name: user.full_name,
                                                                            department_id:
                                                                                department?.department_id ||
                                                                                user.department_id,
                                                                            department_name:
                                                                                department?.department_name ||
                                                                                user.department_name,
                                                                            business_id: user.business_id,
                                                                            user_name: user.user_name || '',
                                                                        },
                                                                    );
                                                                    setBookingInfo({
                                                                        department:
                                                                            department?.department_name ||
                                                                            user.department_name ||
                                                                            '',
                                                                        department_id:
                                                                            department?.department_id ||
                                                                            user.department_id ||
                                                                            '',
                                                                        doctor: user.full_name || '',
                                                                        user_id: user.user_id || '',
                                                                        business_id: user.business_id || '',
                                                                        user_name: user.user_name || '',
                                                                    });

                                                                    // Scroll xuống form đặt lịch
                                                                    const formSection =
                                                                        document.querySelector('.register-section');
                                                                    if (formSection) {
                                                                        const offset = 150; // Khoảng cách từ top
                                                                        const elementPosition = formSection.offsetTop;
                                                                        const offsetPosition = elementPosition - offset;

                                                                        window.scrollTo({
                                                                            top: offsetPosition,
                                                                            behavior: 'instant',
                                                                        });
                                                                    }
                                                                }}>
                                                                <img
                                                                    className="icon"
                                                                    src="/bw_image/ic_calendar_w.png"
                                                                    alt=""
                                                                />
                                                                {getLocaleContent(locale, 'page.doctors.booking')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </article>
                                            </Link>
                                        </SwiperSlide>
                                    ))}
                            </Swiper>
                        </div>
                    </div>

                    <div className="doctors__more">
                        <Link href="/chuyen-gia-bac-si">
                            <a className="svc__all">
                                {getLocaleContent(locale, 'common.see-all')}
                                <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        d="M8 5l8 7-8 7"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </a>
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
